---
title: "How to remove location data from a JPG before sharing"
description: "Check GPS in EXIF, clean a local JPG copy in the browser, then inspect the downloaded file again before you share it."
locale: "en"
date: "2026-08-03"
tags: ["exif", "gps", "metadata", "privacy"]
relatedTools: ["metadataViewer", "removeExif"]
translations:
  ru: "kak-udalit-geolokaciyu-iz-foto-pered-otpravkoj"
faq:
  - q: "Does removing EXIF change the JPG image itself?"
    a: "Yes. Imgify decodes the JPG and creates a new copy at 95% quality, so the file size and individual pixels may change."
  - q: "Can I verify that GPS data is gone from the cleaned JPG?"
    a: "Yes. Download the cleaned copy and open it in the metadata viewer again. The GPS section should be empty."
  - q: "Is the photo uploaded while it is inspected or cleaned?"
    a: "No. Both tools read and process the file locally in the browser. Ordinary page and analytics requests do not contain the photo bytes."
---

The photo for a classified ad was taken outside your home. There is no street
number in the frame, but the JPG may still contain GPS coordinates, capture
time and the phone model. Before sharing the original file, inspect its
metadata rather than trusting the preview.

Open the [EXIF metadata viewer](/posmotret-exif) and select the JPG. It reads
EXIF, GPS, IPTC and XMP locally in the browser. If latitude and longitude appear
in the GPS section, the recipient of the original file can extract them in the
same way. A visible map is optional. The coordinates already identify the
capture point.

## Make a separate copy without GPS

Go to the [EXIF remover](/udalit-exif), add the same JPG and download the
cleaned result. The original stays untouched. Imgify decodes the photo and
writes a new JPEG copy at 95% quality. The source EXIF and XMP blocks are not
carried into that copy.

There is a trade-off: JPEG uses lossy compression. Encoding it again can change
the file size and individual pixels, and preservation of the original ICC
profile is not guaranteed. That is usually acceptable for a one-off upload or
message. Do not replace your archival original with the cleaned copy.

The input is limited to 50 MB, 36 megapixels and 32768 pixels on either side.
If container checks or image decoding fail, imgify reports an error. It does
not upload the photo to a server for a fallback attempt.

## Inspect the file you will actually send

Open the downloaded JPG in the
[metadata viewer](/posmotret-exif) once more. GPS, camera serial number, author
and other recognised fields removed by the re-encode should no longer appear.
This check is stronger than a generic "metadata removed" message because it
examines the exact file leaving your device.

Metadata is not the same as visible information. Removing EXIF cannot hide a
shop sign, vehicle plate, window reflection or recognisable view. Use the
[manual blur tool](/razmyt-foto) for those details and mark the relevant area
yourself.

The remover also accepts PNG and WebP, but their paths are different. For PNG
it removes `eXIf`, text, XMP, and `tIME` chunks; for WebP it removes EXIF and XMP
chunks. In both formats it preserves the image payload. Unknown or proprietary
blocks may remain, so this is not a forensic-cleanup claim. For a sensitive
file, keep the original, clean a copy, inspect that copy, then share it.
